import { Link } from 'react-router-dom'
import { ArrowRight, Users, ShoppingBag, BarChart2, Scissors, Factory, CheckCircle, Star, TrendingUp, Shield, Zap } from 'lucide-react'
import SEO from '../components/SEO'
import './Home.css'

const products = [
  { icon: Users, label: 'Human Capital Management', path: '/products/human-capital-management', desc: 'End-to-end HR operations: payroll, recruitment, attendance, performance, and compliance in one platform.' },
  { icon: ShoppingBag, label: 'Takeaway System', path: '/products/takeaway-system', desc: 'Modern POS and order management for restaurants, cloud kitchens, and multi-outlet food businesses.' },
  { icon: BarChart2, label: 'Account Management', path: '/products/account-management', desc: 'Streamlined financial accounting, invoicing, and reporting for businesses of every scale.' },
  { icon: Scissors, label: 'Textile ERP', path: '/products/textile-erp', desc: 'Specialized ERP for the textile industry covering design, production, fabric inventory, and exports.' },
  { icon: Factory, label: 'Manufacturing Inventory', path: '/products/manufacturing-inventory', desc: 'Real-time inventory control, BOM management, and shop-floor tracking for manufacturers.' },
]

const stats = [
  { value: '200+', label: 'Enterprise Clients' },
  { value: '15+', label: 'Years of Experience' },
  { value: '99.9%', label: 'Uptime SLA' },
  { value: '5', label: 'Industry Verticals' },
]

const whys = [
  { icon: Shield, title: 'Enterprise Security', desc: 'SOC 2 compliant infrastructure with end-to-end encryption and role-based access control.' },
  { icon: Zap, title: 'Rapid Deployment', desc: 'Go live in weeks, not months. Our onboarding team ensures seamless data migration and training.' },
  { icon: TrendingUp, title: 'Scales With You', desc: 'Whether you have 10 or 10,000 employees, our platform grows with your business needs.' },
  { icon: CheckCircle, title: 'Local Compliance', desc: 'Built for Indian regulatory requirements including GST, PF, ESI, TDS, and statutory filings.' },
]

export default function Home() {
  return (
    <>
      <SEO
        title="Enterprise Software Solutions"
        description="NexCore Solutions delivers enterprise-grade software for HCM, Takeaway POS, Accounting, Textile ERP, and Manufacturing Inventory management."
        keywords="HCM software India, ERP solutions, Takeaway POS system, textile ERP, manufacturing inventory software, HR software Chennai"
        path="/"
      />

      {/* Hero */}
      <section className="hero">
        <div className="hero__bg-grid" />
        <div className="container hero__content">
          <div className="hero__badge">Trusted by 200+ enterprises across India</div>
          <h1 className="hero__title">
            Software that powers<br />
            <span className="hero__title-accent">your entire operation</span>
          </h1>
          <p className="hero__sub">
            From managing your workforce to running your factory floor, NexCore builds precise, reliable enterprise software for the industries that keep India moving.
          </p>
          <div className="hero__actions">
            <Link to="/contact" className="btn-primary">Schedule a Demo <ArrowRight size={16} /></Link>
            <Link to="/products/human-capital-management" className="btn-outline hero__outline">Explore Products</Link>
          </div>
        </div>
        <div className="hero__stats">
          <div className="container hero__stats-grid">
            {stats.map(s => (
              <div key={s.label} className="hero__stat">
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Products */}
      <section className="home-products">
        <div className="container">
          <div className="home-products__header">
            <p className="section-label">Our Product Suite</p>
            <h2 className="section-title">Purpose-built software<br />for every vertical</h2>
            <p className="section-subtitle">Each product is designed from the ground up for its specific industry — not adapted from a generic template.</p>
          </div>
          <div className="home-products__grid">
            {products.map(({ icon: Icon, label, path, desc }) => (
              <Link to={path} key={path} className="product-card">
                <div className="product-card__icon"><Icon size={24} /></div>
                <h3>{label}</h3>
                <p>{desc}</p>
                <span className="product-card__link">Learn more <ArrowRight size={14} /></span>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Why NexCore */}
      <section className="home-why">
        <div className="container">
          <div className="home-why__inner">
            <div className="home-why__left">
              <p className="section-label">Why NexCore</p>
              <h2 className="section-title">Built for the way<br />Indian enterprises work</h2>
              <p className="section-subtitle">We don't localize a foreign product. We build from the ground up with Indian compliance, languages, and workflows at the core.</p>
              <Link to="/contact" className="btn-primary" style={{marginTop: '28px'}}>Talk to Our Team <ArrowRight size={16} /></Link>
            </div>
            <div className="home-why__grid">
              {whys.map(({ icon: Icon, title, desc }) => (
                <div key={title} className="why-card">
                  <div className="why-card__icon"><Icon size={20} /></div>
                  <h4>{title}</h4>
                  <p>{desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="home-testimonial">
        <div className="container">
          <div className="testimonial-card">
            <div className="testimonial-card__stars">
              {[...Array(5)].map((_, i) => <Star key={i} size={16} fill="currentColor" />)}
            </div>
            <blockquote>"Switching to NexCore's HCM platform reduced our monthly payroll processing time by 70%. The compliance automation alone saved us from three audit findings last year."</blockquote>
            <div className="testimonial-card__author">
              <div className="testimonial-card__avatar">SK</div>
              <div>
                <strong>Sridhar Krishnamurthy</strong>
                <span>Head of HR, Arunachala Mills Ltd.</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="home-cta">
        <div className="container">
          <div className="home-cta__inner">
            <h2>Ready to modernize your operations?</h2>
            <p>Get a personalized demo with our product specialists. No commitment required.</p>
            <div className="home-cta__actions">
              <Link to="/contact" className="btn-primary">Request a Demo <ArrowRight size={16} /></Link>
              <Link to="/blogs" className="btn-outline home-cta__outline">Read Our Blog</Link>
            </div>
          </div>
        </div>
      </section>
    </>
  )
}
